import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PublicIcon from "@mui/icons-material/Public";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import {
  EventInvitee,
  EventParticipant,
} from "src/app/models/screens/events.model";
import { File as CloudieFile } from "src/app/models/screens/files.model";
import EventDetailScreenViewModel from "src/app/screens/eventDetail/eventDetail.viewmodel";
import styles from "src/app/styles/screens/eventDetail.style";
import { getEventBannerUrl } from "src/app/utils/backendEndpointCalls.util";

const ACCEPTED_MEDIA_TYPES = "image/*,video/*";

const EventDetailScreenView: React.FC = () => {
  const vm = EventDetailScreenViewModel();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const goBack = () => navigate("/events");

  if (vm.isLoadingDetail || !vm.event) {
    return (
      <Box sx={styles.parent}>
        <DrawerComponentView selectedId="Events" />
        <DrawerAwareContainerComponentView
          drawerWidth={250}
          sx={styles.drawerAwareContainer}
        >
          <Box sx={styles.searchingLoader}>
            <CircularProgress size={32} />
          </Box>
        </DrawerAwareContainerComponentView>
      </Box>
    );
  }

  const event = vm.event;
  const canShowMediaSection =
    (vm.viewerIsCreator || vm.viewerIsParticipant) && !!event.cloudieFolderId;
  const canUploadMedia = canShowMediaSection && vm.eventHasFinished;

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Events" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_MEDIA_TYPES}
          style={{ display: "none" }}
          onChange={(e) => vm.onMediaFilesSelected(e.target.files)}
          onClick={(e) => ((e.target as HTMLInputElement).value = "")}
        />
        <Box sx={styles.backRow}>
          <IconButton onClick={goBack} sx={styles.backButton} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ opacity: 0.6, fontSize: 14 }}>
            Back to events
          </Typography>
        </Box>
        {vm.userRole === "Parent" && (
          <Grid size={12} sx={styles.selectorContainer} justifySelf="flex-end">
            <Grid
              size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
              sx={styles.selectorPaper}
            >
              <FormControl fullWidth>
                <InputLabel id="event-detail-owner-label">Acting As</InputLabel>
                <Select
                  labelId="event-detail-owner-label"
                  value={vm.selectedOwnerId}
                  label="Acting As"
                  onChange={(e: SelectChangeEvent) =>
                    vm.onOwnerChanged(e.target.value)
                  }
                  disabled={vm.isLoadingChildren}
                >
                  <MenuItem value="you">You</MenuItem>
                  {vm.children.map((child) => (
                    <MenuItem key={child._id} value={child._id}>
                      {child.firstName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        <Grid container justifyContent="center" spacing={3}>
          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            {event.hasBanner ? (
              <Box
                component="img"
                src={getEventBannerUrl(event.id)}
                alt={event.title}
                sx={styles.banner}
              />
            ) : (
              <Box sx={styles.bannerFallback}>
                <CelebrationOutlinedIcon fontSize="inherit" />
              </Box>
            )}
            <Box sx={styles.titleRow}>
              <Typography sx={styles.titleText}>{event.title}</Typography>
            </Box>
            <Box sx={styles.chipsRow}>
              <Chip
                size="small"
                icon={event.isPublic ? <PublicIcon /> : <LockOutlinedIcon />}
                label={event.isPublic ? "Public" : "Private"}
                variant="outlined"
              />
              {event.isPaid && (
                <Chip
                  size="small"
                  icon={<PaymentsOutlinedIcon />}
                  label={`${vm.formatCurrency(
                    event.participationFee,
                    event.currency,
                  )} per participant`}
                  color="warning"
                  variant="outlined"
                />
              )}
              {vm.eventHasFinished && (
                <Chip size="small" label="Finished" color="default" />
              )}
              {event.isCancelled && (
                <Chip size="small" label="Cancelled" color="error" />
              )}
              {event.paymentsProcessed && (
                <Chip
                  size="small"
                  label="Payments processed"
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
            <Box sx={styles.metaRow}>
              <Box sx={styles.metaItem}>
                <EventOutlinedIcon fontSize="small" sx={styles.metaIcon} />
                <Box>
                  <Typography sx={styles.metaText}>{event.date}</Typography>
                  <Typography sx={styles.metaSecondary}>Date</Typography>
                </Box>
              </Box>
              <Box sx={styles.metaItem}>
                <AccessTimeIcon fontSize="small" sx={styles.metaIcon} />
                <Box>
                  <Typography sx={styles.metaText}>
                    {vm.minutesToLabel(event.from)} –{" "}
                    {vm.minutesToLabel(event.to)}
                  </Typography>
                  <Typography sx={styles.metaSecondary}>
                    {event.durationMinutes} min
                  </Typography>
                </Box>
              </Box>
              <Box sx={styles.metaItem}>
                <LocationOnOutlinedIcon fontSize="small" sx={styles.metaIcon} />
                <Box>
                  <Typography sx={styles.metaText}>
                    {event.location || "—"}
                  </Typography>
                  <Typography sx={styles.metaSecondary}>Location</Typography>
                </Box>
              </Box>
              <Box sx={styles.metaItem}>
                <PersonAddIcon fontSize="small" sx={styles.metaIcon} />
                <Box>
                  <Typography sx={styles.metaText}>
                    {event.creatorFullName}
                  </Typography>
                  <Typography sx={styles.metaSecondary}>Creator</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={styles.actionsRow}>
              {vm.viewerIsCreator && (
                <Button
                  variant="contained"
                  startIcon={<GroupAddIcon />}
                  onClick={vm.openInviteDialog}
                >
                  Invite Users
                </Button>
              )}
              {vm.viewerIsCreator && (
                <Button
                  variant="outlined"
                  startIcon={<ManageAccountsOutlinedIcon />}
                  onClick={vm.openMediaAccessDialog}
                >
                  Manage Media Access
                </Button>
              )}
              {vm.canReschedule && (
                <Button
                  variant="outlined"
                  startIcon={<EditCalendarIcon />}
                  onClick={vm.openRescheduleDialog}
                >
                  Reschedule
                </Button>
              )}
              {vm.canCancel && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<CancelOutlinedIcon />}
                  onClick={vm.openCancelDialog}
                >
                  Cancel Event
                </Button>
              )}
              {vm.canWithdraw && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<ExitToAppIcon />}
                  onClick={vm.openWithdrawDialog}
                >
                  Withdraw
                </Button>
              )}
            </Box>
            {event.description && (
              <>
                <Typography sx={styles.sectionTitle}>About</Typography>
                <Paper elevation={0} sx={styles.contentCard}>
                  <Typography sx={styles.description}>
                    {event.description}
                  </Typography>
                </Paper>
              </>
            )}
            <Typography sx={styles.sectionTitle}>
              Participants ({vm.participants.length})
            </Typography>
            <Box sx={styles.participantList}>
              {vm.participants.length === 0 ? (
                <Typography sx={styles.noItemsText}>
                  No participants yet.
                </Typography>
              ) : (
                vm.participantGroups.map((group) => {
                  const isAnchorCreator = group.anchor.id === event.creatorId;
                  return (
                    <Box
                      key={group.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 0.75,
                        p: group.others.length > 0 ? 1 : 0,
                        borderRadius: 2,
                        border:
                          group.others.length > 0
                            ? "1px dashed rgba(0,0,0,0.12)"
                            : "none",
                        backgroundColor:
                          group.others.length > 0
                            ? "rgba(0,0,0,0.02)"
                            : "transparent",
                      }}
                    >
                      <Chip
                        label={group.anchor.fullName}
                        sx={styles.participantChip}
                        color={isAnchorCreator ? "primary" : "default"}
                        variant={isAnchorCreator ? "filled" : "outlined"}
                      />
                      {group.others.length > 0 && (
                        <Typography
                          component="span"
                          sx={{ fontSize: 13, opacity: 0.7 }}
                        >
                          with
                        </Typography>
                      )}
                      {group.others.map((other) => {
                        const isOtherCreator = other.id === event.creatorId;
                        return (
                          <Chip
                            key={other.id}
                            label={other.fullName}
                            size="small"
                            sx={styles.participantChip}
                            color={isOtherCreator ? "primary" : "default"}
                            variant={isOtherCreator ? "filled" : "outlined"}
                          />
                        );
                      })}
                    </Box>
                  );
                })
              )}
            </Box>

            {canShowMediaSection && (
              <>
                <Typography sx={styles.sectionTitle}>Event Media</Typography>
                {!vm.eventHasFinished ? (
                  <Paper elevation={0} sx={styles.contentCard}>
                    <Typography sx={styles.mediaIntro}>
                      Photos and videos can be uploaded here once the event has
                      ended. They are stored in a dedicated folder on Cloudie
                      and shared with all participants.
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    {canUploadMedia && (
                      <Box
                        sx={styles.uploadBox}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <AddPhotoAlternateOutlinedIcon sx={styles.uploadIcon} />
                        <Typography sx={styles.uploadHint}>
                          Click to upload photos or videos from this event
                        </Typography>
                      </Box>
                    )}

                    {vm.isLoadingEventMedia ? (
                      <Box sx={styles.searchingLoader}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : vm.eventMedia.length === 0 ? (
                      <Typography sx={styles.noItemsText}>
                        No media uploaded yet.
                      </Typography>
                    ) : (
                      <Box sx={styles.mediaGrid}>
                        {vm.eventMedia.map((file: CloudieFile) => {
                          const isVideo = file.mimeType?.startsWith("video/");
                          return (
                            <Box
                              key={file.id}
                              sx={styles.mediaTile}
                              onClick={() => {
                                const url = vm.mediaUrl(file, false);
                                window.open(url, "_blank", "noopener");
                              }}
                            >
                              <Box
                                component="img"
                                src={vm.mediaUrl(file, true)}
                                alt={file.name}
                                sx={styles.mediaImg}
                                loading="lazy"
                              />
                              {isVideo && (
                                <PlayCircleIcon sx={styles.mediaPlayBadge} />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Grid>
        </Grid>
        {vm.uploadTasks.length > 0 && (
          <Box sx={styles.uploadingPanel}>
            {vm.uploadTasks.map((task) => (
              <Paper key={task.id} elevation={0} sx={styles.uploadingCard}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 220,
                    }}
                  >
                    {task.fileName}
                  </Typography>
                  {task.done && (
                    <IconButton
                      size="small"
                      onClick={() => vm.dismissUploadTask(task.id)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={task.progress}
                  color={task.error ? "error" : "primary"}
                />
                <Typography sx={{ fontSize: 11, opacity: 0.7, mt: 0.5 }}>
                  {task.error
                    ? task.error
                    : task.done
                      ? "Uploaded"
                      : `${task.progress}%`}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </DrawerAwareContainerComponentView>
      <Dialog
        open={vm.isInviteDialogOpen}
        onClose={vm.closeInviteDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Invite Users</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Search users"
            value={vm.inviteSearchText}
            onChange={(e) => vm.setInviteSearchText(e.target.value)}
            sx={styles.searchBox}
            autoFocus
          />
          {vm.isSearchingInvitees ? (
            <Box sx={styles.searchingLoader}>
              <CircularProgress size={24} />
            </Box>
          ) : vm.inviteSearchResults.length === 0 ? (
            <Typography sx={styles.noItemsText}>
              {vm.inviteSearchText.trim()
                ? "No users found."
                : "Start typing to find users to invite."}
            </Typography>
          ) : (
            vm.inviteSearchResults.map((user: EventInvitee) => (
              <Box key={user.id} sx={styles.inviteListItem}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => vm.onInviteUser(user)}
                  loading={vm.invitingUserId === user.id}
                >
                  Invite
                </Button>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={vm.closeInviteDialog}
            color="inherit"
            sx={styles.modalCancelButton}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={vm.isMediaAccessDialogOpen}
        onClose={vm.closeMediaAccessDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Manage Media Access</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...styles.mediaIntro, mb: 2 }}>
            All participants have access to the event's media folder by default.
            Toggle off any participant you want to exclude from this event's
            photos and videos.
          </Typography>
          {vm.participants
            .filter((p) => p.id !== event.creatorId)
            .map((p: EventParticipant) => {
              const allow = !p.excludedFromMedia;
              return (
                <Box key={p.id} sx={styles.accessRow}>
                  <Typography sx={styles.accessRowName}>
                    {p.fullName}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allow}
                        onChange={(e) =>
                          vm.onToggleParticipantMediaAccess(
                            p.id,
                            e.target.checked,
                          )
                        }
                        disabled={vm.updatingMediaUserId === p.id}
                      />
                    }
                    label={allow ? "Allowed" : "Excluded"}
                    labelPlacement="start"
                  />
                </Box>
              );
            })}
          {vm.participants.filter((p) => p.id !== event.creatorId).length ===
            0 && (
            <Typography sx={styles.noItemsText}>
              There are no participants other than yourself yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={vm.closeMediaAccessDialog}
            color="inherit"
            sx={styles.modalCancelButton}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={vm.isCancelDialogOpen}
        onClose={vm.closeCancelDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cancel Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this event? Participants will be
            notified and the event will be marked as cancelled.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={vm.closeCancelDialog}
            color="inherit"
            variant="outlined"
          >
            Keep Event
          </Button>
          <Button
            onClick={vm.onConfirmCancel}
            variant="contained"
            color="error"
            loading={vm.isCancelling}
          >
            Cancel Event
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={vm.isRescheduleDialogOpen}
        onClose={vm.closeRescheduleDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Reschedule Event</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, opacity: 0.75, fontSize: 13 }}>
            Pick a new date, start time, and duration. The new time must be in
            the future.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 1,
            }}
          >
            <DatePicker
              label="New Date"
              value={vm.rescheduleDate}
              onChange={vm.setRescheduleDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <TimePicker
              label="Start Time"
              value={vm.rescheduleStartTime}
              onChange={vm.setRescheduleStartTime}
              ampm={false}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <TextField
              fullWidth
              label="Duration (minutes)"
              type="number"
              value={vm.rescheduleDurationMinutes}
              onChange={(e) => vm.setRescheduleDurationMinutes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={vm.closeRescheduleDialog}
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={vm.onConfirmReschedule}
            variant="contained"
            loading={vm.isRescheduling}
          >
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={vm.isWithdrawDialogOpen}
        onClose={vm.closeWithdrawDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Withdraw from Event</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please provide a reason for withdrawal. Withdrawals are
            auto-accepted only for valid causes (e.g. emergency, illness, family
            circumstances).
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Reason"
            value={vm.withdrawReason}
            onChange={(e) => vm.setWithdrawReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={vm.closeWithdrawDialog}
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={vm.onConfirmWithdraw}
            variant="contained"
            color="error"
            loading={vm.isWithdrawing}
          >
            Submit Withdrawal
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponentView
        isError={vm.isSnackbarError}
        isOpen={vm.isDisplayingSnackbar}
        message={vm.snackbarMessage}
        onClose={() => vm.setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default EventDetailScreenView;
